import { Box, Button, Card, Container, useMediaQuery } from '@mui/material'
import { Alert } from '@mui/material'
import { Form, Formik, isFunction, validateYupSchema, yupToFormErrors } from 'formik'
import PropTypes from 'prop-types'
import { identity, gt } from 'ramda'
import React, { useCallback, useState } from 'react'
import { JsonView } from 'react-json-view-lite'
import 'react-json-view-lite/dist/index.css'
import appModules from '../../lib/util/app-modules'
import ConfirmDialog from '../controls/ConfirmDialog'
import ThemedButton from '../controls/ThemedButton'
import ActionHeader from '../elements/ActionHeader'
import ActivityList from '../elements/ActivityList'

export const Submit = ({ submitLabel, creating, isSubmitting }) => (
  <Button color="primary" type="submit" size="large" disabled={isSubmitting}>
    {submitLabel || (creating ? 'Create' : 'Save')}
  </Button>
)

Submit.propTypes = {
  submitLabel: PropTypes.node,
  creating: PropTypes.bool,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  submitForm: PropTypes.func,
  isSubmitting: PropTypes.bool,
}

export const SubmitActivate = ({ values, setFieldValue, submitForm, creating, isSubmitting, activeField = 'active' }) => {
  const { [activeField]: active } = values
  const action = creating ? 'Create' : 'Save'
  return (
    <>
      <Button
        color="primary"
        size="large"
        disabled={isSubmitting}
        onClick={() => {
          if (!isSubmitting) {
            setFieldValue(activeField, true)
            submitForm()
          }
        }}
      >
        {action} {!active && 'and activate'}
      </Button>
      <Box mx={2}>
        <Button
          color="primary"
          variant="outlined"
          size="large"
          disabled={isSubmitting}
          onClick={() => {
            if (!isSubmitting) {
              setFieldValue(activeField, false)
              submitForm()
            }
          }}
        >
          {action} {active && (creating ? 'as draft' : 'and deactivate')}
        </Button>
      </Box>
    </>
  )
}

SubmitActivate.propTypes = Submit.propTypes

const Remove = ({ isSubmitting, onClick }) => (
  <ThemedButton variant="outlined" color="danger" size="large" type="button" disabled={isSubmitting} onClick={onClick}>
    Delete
  </ThemedButton>
)

Remove.propTypes = {
  isSubmitting: PropTypes.bool,
  onClick: PropTypes.func,
}

const debugFromStorage = localStorage.getItem('debugAdminForm')

const defaultTitle = ({ type, creating, values }) => {
  const appModule = (type && appModules[type]) || {}
  const { displayType = type } = appModule
  let title = `${creating ? 'New' : 'Editing'} ${displayType}`

  if (!creating && appModule.title) {
    title = `${title}: ${appModule.title(values)}`
  }
  return title
}

const ValidatedForm = ({
  schema,
  schemaContext = {},
  initialValues = {},
  children,
  submitLabel,
  actionButtons,
  activity = [],
  title,
  section,
  action,
  type,
  submitComponent,
  submitComponentProps,
  cardComponent,
  submitExtra,
  submitExtraContainerStyle = {},
  onCancel,
  error,
  // This is an unpleasant workaround for https://github.com/formium/formik/issues/568
  transformInitialValues = identity,
  debug,
  removeComponent,
  handleRemove,
  enableReinitialize = true,
  hideTitleSection = false,
  intro,
  ...rest
}) => {
  const SubmitComponent = submitComponent || Submit
  const RemoveComponent = removeComponent || Remove
  const isSmallOrLower = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const CardComponent = cardComponent || (isSmallOrLower ? React.Fragment : Card)

  const [modalVisible, setModalVisible] = useState(false)

  // Copied from Formik, so we can expose context to Yup during validation.
  const emptyErrors = {}
  const runValidationSchema = React.useCallback(
    (values, field) => {
      const validationSchema = isFunction(schema) ? schema(field) : schema
      const promise =
        field && validationSchema.validateAt ? validationSchema.validateAt(field, values) : validateYupSchema(values, validationSchema, false, schemaContext) // Here, we pass in context.
      return new Promise((resolve, reject) => {
        promise.then(
          () => {
            resolve(emptyErrors)
          },
          (err) => {
            // Yup will throw a validation error if validation fails. We catch those and
            // resolve them into Formik errors. We can sniff if something is a Yup error
            // by checking error.name.
            // @see https://github.com/jquense/yup#validationerrorerrors-string--arraystring-value-any-path-string
            if (err.name === 'ValidationError') {
              resolve(yupToFormErrors(err))
            } else {
              // We throw any other errors
              if (process.env.NODE_ENV !== 'production') {
                console.warn(`Warning: An unhandled error was caught during validation in <Formik validationSchema />`, err)
              }

              reject(err)
            }
          }
        )
      })
    },
    [schema, schemaContext]
  )

  return (
    <Formik
      enableReinitialize={enableReinitialize}
      initialValues={transformInitialValues(schema.cast(initialValues, { context: schemaContext }))}
      validate={runValidationSchema}
      {...rest}
    >
      {(props) => {
        const { values, errors, dirty, touched } = props
        // Title should always be a string, but we allow a function that returns a string.
        let valueTitle
        if (typeof title === 'function') {
          valueTitle = title(values)
        }
        let valueSubmitExtra
        if (typeof submitExtra === 'function') {
          valueSubmitExtra = submitExtra(props)
        }

        const creating = !values._id || action === 'duplicate'
        const activities = !creating && <ActivityList activity={activity} values={values} />

        const moduleDisplayType = appModules[type]?.displayType || type
        const removeDialogue = (
          <ConfirmDialog
            title={`Delete ${moduleDisplayType}`}
            fullWidth
            onClose={() => setModalVisible(false)}
            onConfirm={() => {
              setModalVisible(false)
              handleRemove()
            }}
            confirmText="OK"
            open={modalVisible}
          >
            Are you sure you want to delete this <strong>{moduleDisplayType}</strong>?
          </ConfirmDialog>
        )

        const navigateBack = useCallback(() => window.history.back())

        const titleSection = (title || type) && (
          <ActionHeader title={valueTitle || title || defaultTitle({ type, creating, values })} section={section}>
            <Box ml={2}>
              {actionButtons}
              <Button size="large" variant="text" onClick={onCancel || navigateBack}>
                Cancel
              </Button>
            </Box>
          </ActionHeader>
        )

        return (
          <Container maxWidth="md">
            {removeDialogue}
            <Box pb={6}>
              <Form>
                {intro}
                {hideTitleSection ? null : titleSection}

                {Boolean(debug || debugFromStorage) && (
                  <>
                    <Box margin={1}>
                      <JsonView data={values} shouldExpandNode={gt(1)} />
                    </Box>
                    <Box margin={1}>
                      <JsonView data={{ errors, dirty, touched }} shouldExpandNode={gt(1)} />
                    </Box>
                  </>
                )}
                <CardComponent>
                  <Box p={isSmallOrLower ? 0 : 6}>
                    {typeof children === 'function' ? children(props) : children}
                    {error && (
                      <Box my={2}>
                        <Alert severity="error">{error.message}</Alert>
                      </Box>
                    )}
                    <Box mt={3} mb={activities ? 5 : 1} display="flex" alignItems="center" flexDirection="row-reverse">
                      <SubmitComponent {...submitComponentProps} {...props} submitLabel={submitLabel} creating={creating} />
                      {submitExtra && (
                        <Box mr={3} style={submitExtraContainerStyle}>
                          {valueSubmitExtra || submitExtra}
                        </Box>
                      )}
                      {handleRemove && !creating && (
                        <Box mr={3} style={{ flex: 1 }}>
                          <RemoveComponent onClick={() => setModalVisible(true)} isSubmitting={props.isSubmitting} />
                        </Box>
                      )}
                    </Box>
                    {activities}
                  </Box>
                </CardComponent>
              </Form>
            </Box>
          </Container>
        )
      }}
    </Formik>
  )
}

ValidatedForm.propTypes = {
  ...Formik.propTypes,
  actionButtons: PropTypes.node,
  intro: PropTypes.node,
  schema: PropTypes.object,
  activity: PropTypes.array,
  submitLabel: PropTypes.string,
}

export default ValidatedForm
